import type { Accent, FontPairing, Pattern, Skin, SkinTokens } from "./types";

// ─── Skin builder helpers ───────────────────────────────────────────────────
// Each skin defines a complete light + dark token bundle. Values are HSL
// triplets so they slot directly into the existing `hsl(var(--...))` setup
// in `index.css`. Defaults match today's "Classic Teal" look exactly so
// existing teachers see no visual change.

function classicTealLight(): SkinTokens {
  return {
    background: "216 20% 98%",      /* #f8f9fb */
    foreground: "220 14% 26%",      /* #374151 */
    border: "210 14% 95%",          /* #f0f2f4 */
    input: "210 14% 95%",
    ring: "164 72% 40%",            /* #1cb08a */
    card: "0 0% 100%",
    "card-foreground": "222 45% 18%",  /* #1a2744 */
    "card-border": "210 14% 95%",
    popover: "0 0% 100%",
    "popover-foreground": "222 45% 18%",
    "popover-border": "210 14% 95%",
    primary: "164 72% 40%",         /* #1cb08a */
    "primary-foreground": "0 0% 100%",
    "primary-border": "172 78% 27%", /* #0f7a6b */
    secondary: "210 20% 95%",
    "secondary-foreground": "220 14% 26%",
    "secondary-border": "210 14% 91%",
    muted: "210 20% 96%",
    "muted-foreground": "220 9% 65%", /* #9ca3af */
    "muted-border": "210 14% 91%",
    accent: "151 57% 92%",          /* #e1f5ee */
    "accent-foreground": "167 82% 17%", /* #085041 */
    "accent-border": "151 40% 82%",
    destructive: "1 74% 59%",       /* #e24b4a */
    "destructive-foreground": "0 0% 100%",
    "destructive-border": "1 74% 48%",
    sidebar: "0 0% 100%",           /* white — neutral chrome */
    "sidebar-foreground": "220 9% 46%",    /* #6b7280 */
    "sidebar-border": "210 14% 95%",
    "sidebar-primary": "164 72% 40%",      /* #1cb08a active pill */
    "sidebar-primary-foreground": "0 0% 100%",
    "sidebar-primary-border": "172 78% 27%",
    "sidebar-accent": "216 20% 96%",
    "sidebar-accent-foreground": "220 9% 35%",
    "sidebar-accent-border": "210 14% 92%",
    "sidebar-ring": "164 72% 40%",
  };
}

// Baseline dark = the existing `.dark { ... }` block in index.css.
function baselineDark(): SkinTokens {
  return {
    background: "222.2 84% 4.9%",
    foreground: "210 40% 98%",
    border: "217.2 32.6% 17.5%",
    input: "217.2 32.6% 17.5%",
    ring: "224.3 76.3% 48%",
    card: "222.2 84% 4.9%",
    "card-foreground": "210 40% 98%",
    "card-border": "217.2 32.6% 17.5%",
    popover: "222.2 84% 4.9%",
    "popover-foreground": "210 40% 98%",
    "popover-border": "217.2 32.6% 17.5%",
    primary: "217 91% 60%",
    "primary-foreground": "0 0% 100%",
    "primary-border": "217 91% 50%",
    secondary: "217.2 32.6% 17.5%",
    "secondary-foreground": "210 40% 98%",
    "secondary-border": "217.2 32.6% 25%",
    muted: "217.2 32.6% 17.5%",
    "muted-foreground": "215 20.2% 65.1%",
    "muted-border": "217.2 32.6% 25%",
    accent: "217.2 32.6% 17.5%",
    "accent-foreground": "210 40% 98%",
    "accent-border": "217.2 32.6% 25%",
    destructive: "0 62.8% 30.6%",
    "destructive-foreground": "210 40% 98%",
    "destructive-border": "0 62.8% 40%",
    sidebar: "222.2 84% 4.9%",
    "sidebar-foreground": "210 40% 98%",
    "sidebar-border": "217.2 32.6% 17.5%",
    "sidebar-primary": "217 91% 60%",
    "sidebar-primary-foreground": "0 0% 100%",
    "sidebar-primary-border": "217 91% 50%",
    "sidebar-accent": "217.2 32.6% 17.5%",
    "sidebar-accent-foreground": "210 40% 98%",
    "sidebar-accent-border": "217.2 32.6% 25%",
    "sidebar-ring": "217 91% 60%",
  };
}

/**
 * Build a "soft paper" style light skin from a small set of palette
 * inputs. Keeps each skin definition readable while still producing a
 * full token bundle. Values are HSL triplets.
 */
function buildLight(p: {
  bg: string;
  fg: string;
  card: string;
  border: string;
  muted: string;
  mutedFg: string;
  primary: string;
  primaryFg: string;
  accent: string;
  accentFg: string;
  sidebar: string;
  sidebarFg: string;
  sidebarAccent: string;
}): SkinTokens {
  return {
    background: p.bg,
    foreground: p.fg,
    border: p.border,
    input: p.border,
    ring: p.primary,
    card: p.card,
    "card-foreground": p.fg,
    "card-border": p.border,
    popover: p.card,
    "popover-foreground": p.fg,
    "popover-border": p.border,
    primary: p.primary,
    "primary-foreground": p.primaryFg,
    "primary-border": p.primary,
    secondary: p.muted,
    "secondary-foreground": p.fg,
    "secondary-border": p.border,
    muted: p.muted,
    "muted-foreground": p.mutedFg,
    "muted-border": p.border,
    accent: p.accent,
    "accent-foreground": p.accentFg,
    "accent-border": p.border,
    destructive: "0 70% 50%",
    "destructive-foreground": "0 0% 100%",
    "destructive-border": "0 70% 42%",
    sidebar: p.sidebar,
    "sidebar-foreground": p.sidebarFg,
    "sidebar-border": p.sidebar,
    "sidebar-primary": p.primary,
    "sidebar-primary-foreground": p.primaryFg,
    "sidebar-primary-border": p.primary,
    "sidebar-accent": p.sidebarAccent,
    "sidebar-accent-foreground": p.sidebarFg,
    "sidebar-accent-border": p.sidebarAccent,
    "sidebar-ring": p.primary,
  };
}

function buildDark(p: {
  bg: string;
  fg: string;
  card: string;
  border: string;
  muted: string;
  mutedFg: string;
  primary: string;
  primaryFg: string;
  accent: string;
  accentFg: string;
  sidebar: string;
  sidebarFg: string;
  sidebarAccent: string;
}): SkinTokens {
  return buildLight(p);
}

// ─── Skin catalogue ─────────────────────────────────────────────────────────
export const SKINS: Skin[] = [
  {
    id: "classic-teal",
    label: "Classic Teal",
    description: "Clean white sidebar with a fresh teal accent — the default ClassTrack look.",
    preview: { bg: "#f8f9fb", sidebar: "#ffffff", accent: "#1cb08a", text: "#1a2744" },
    defaultAccent: "teal",
    light: classicTealLight(),
    dark: baselineDark(),
  },
  {
    id: "warm-paper",
    label: "Warm Paper",
    description: "Cream paper, warm taupe accents and a quiet serif feel — Papier-style calm.",
    preview: { bg: "#f6efe2", sidebar: "#3b2f24", accent: "#a37b4d", text: "#3b2f24" },
    defaultAccent: "taupe",
    light: buildLight({
      bg: "38 45% 93%",
      fg: "30 25% 18%",
      card: "38 50% 96%",
      border: "34 25% 82%",
      muted: "36 30% 90%",
      mutedFg: "30 15% 40%",
      primary: "28 35% 40%",
      primaryFg: "38 45% 96%",
      accent: "34 35% 86%",
      accentFg: "28 40% 28%",
      sidebar: "30 22% 18%",
      sidebarFg: "38 35% 90%",
      sidebarAccent: "30 18% 26%",
    }),
    dark: buildDark({
      bg: "30 18% 10%",
      fg: "38 30% 90%",
      card: "30 18% 14%",
      border: "30 15% 22%",
      muted: "30 15% 18%",
      mutedFg: "38 15% 65%",
      primary: "30 45% 65%",
      primaryFg: "30 30% 12%",
      accent: "30 18% 22%",
      accentFg: "38 30% 88%",
      sidebar: "30 22% 8%",
      sidebarFg: "38 30% 88%",
      sidebarAccent: "30 18% 16%",
    }),
  },
  {
    id: "soft-sage",
    label: "Soft Sage",
    description: "Sage and cream with blush accents — Mrs Edgar neutrals.",
    preview: { bg: "#f1eee4", sidebar: "#4f5b46", accent: "#8a9a7b", text: "#37402f" },
    defaultAccent: "sage",
    light: buildLight({
      bg: "60 18% 94%",
      fg: "100 12% 18%",
      card: "60 25% 97%",
      border: "90 12% 82%",
      muted: "80 15% 90%",
      mutedFg: "100 8% 40%",
      primary: "100 15% 38%",
      primaryFg: "60 25% 96%",
      accent: "10 30% 88%",
      accentFg: "10 25% 30%",
      sidebar: "100 12% 28%",
      sidebarFg: "60 18% 92%",
      sidebarAccent: "100 12% 36%",
    }),
    dark: buildDark({
      bg: "100 12% 10%",
      fg: "60 18% 90%",
      card: "100 12% 14%",
      border: "100 10% 22%",
      muted: "100 10% 18%",
      mutedFg: "60 12% 65%",
      primary: "100 25% 60%",
      primaryFg: "100 25% 10%",
      accent: "10 20% 24%",
      accentFg: "10 30% 88%",
      sidebar: "100 12% 8%",
      sidebarFg: "60 18% 88%",
      sidebarAccent: "100 12% 16%",
    }),
  },
  {
    id: "blush",
    label: "Blush",
    description: "Dusty pink on warm white with charcoal text. Soft and bright.",
    preview: { bg: "#fbf3ef", sidebar: "#3a2a30", accent: "#c98a8a", text: "#2c1d22" },
    defaultAccent: "rose",
    light: buildLight({
      bg: "20 50% 96%",
      fg: "340 12% 16%",
      card: "20 55% 98%",
      border: "20 25% 88%",
      muted: "20 30% 92%",
      mutedFg: "340 8% 42%",
      primary: "350 35% 55%",
      primaryFg: "20 50% 98%",
      accent: "350 40% 90%",
      accentFg: "350 35% 32%",
      sidebar: "340 18% 20%",
      sidebarFg: "20 30% 92%",
      sidebarAccent: "340 18% 28%",
    }),
    dark: buildDark({
      bg: "340 18% 10%",
      fg: "20 30% 90%",
      card: "340 18% 14%",
      border: "340 12% 24%",
      muted: "340 12% 20%",
      mutedFg: "20 15% 65%",
      primary: "350 60% 70%",
      primaryFg: "340 30% 12%",
      accent: "340 15% 24%",
      accentFg: "20 30% 90%",
      sidebar: "340 22% 8%",
      sidebarFg: "20 30% 90%",
      sidebarAccent: "340 18% 16%",
    }),
  },
  {
    id: "linen",
    label: "Linen",
    description: "Off-white linen with camel accents and ink. Minimalist editorial.",
    preview: { bg: "#f4efe6", sidebar: "#1a1a1a", accent: "#b58a52", text: "#1a1a1a" },
    defaultAccent: "camel",
    light: buildLight({
      bg: "40 30% 94%",
      fg: "0 0% 10%",
      card: "40 35% 97%",
      border: "40 15% 82%",
      muted: "40 20% 90%",
      mutedFg: "0 0% 40%",
      primary: "32 40% 38%",
      primaryFg: "40 30% 96%",
      accent: "40 25% 88%",
      accentFg: "32 35% 28%",
      sidebar: "0 0% 10%",
      sidebarFg: "40 25% 92%",
      sidebarAccent: "0 0% 18%",
    }),
    dark: buildDark({
      bg: "0 0% 8%",
      fg: "40 25% 92%",
      card: "0 0% 12%",
      border: "0 0% 20%",
      muted: "0 0% 16%",
      mutedFg: "0 0% 60%",
      primary: "32 50% 60%",
      primaryFg: "0 0% 10%",
      accent: "0 0% 18%",
      accentFg: "40 25% 88%",
      sidebar: "0 0% 6%",
      sidebarFg: "40 25% 92%",
      sidebarAccent: "0 0% 12%",
    }),
  },
  {
    id: "midnight",
    label: "Midnight",
    description: "Deep navy with brass accent — built for late-night planning.",
    preview: { bg: "#0f1a2e", sidebar: "#0a1322", accent: "#c89a4d", text: "#e8e6df" },
    defaultAccent: "brass",
    light: buildLight({
      bg: "215 30% 96%",
      fg: "215 60% 12%",
      card: "215 30% 98%",
      border: "215 20% 88%",
      muted: "215 20% 92%",
      mutedFg: "215 15% 40%",
      primary: "40 60% 45%",
      primaryFg: "215 60% 10%",
      accent: "40 50% 90%",
      accentFg: "40 60% 28%",
      sidebar: "215 60% 12%",
      sidebarFg: "215 30% 92%",
      sidebarAccent: "215 50% 20%",
    }),
    dark: buildDark({
      bg: "215 50% 8%",
      fg: "40 25% 90%",
      card: "215 45% 12%",
      border: "215 35% 22%",
      muted: "215 40% 16%",
      mutedFg: "215 20% 65%",
      primary: "40 70% 60%",
      primaryFg: "215 60% 10%",
      accent: "215 40% 18%",
      accentFg: "40 50% 85%",
      sidebar: "215 60% 6%",
      sidebarFg: "40 25% 90%",
      sidebarAccent: "215 50% 14%",
    }),
  },
  {
    id: "forest",
    label: "Forest",
    description: "Deep green and cream with warm gold accents. Steady and grounded.",
    preview: { bg: "#f3efe2", sidebar: "#1f3a2c", accent: "#c89a3b", text: "#1f2a22" },
    defaultAccent: "gold",
    light: buildLight({
      bg: "50 30% 94%",
      fg: "140 20% 14%",
      card: "50 35% 97%",
      border: "100 15% 82%",
      muted: "80 18% 90%",
      mutedFg: "140 10% 40%",
      primary: "150 35% 28%",
      primaryFg: "50 30% 96%",
      accent: "45 50% 88%",
      accentFg: "150 35% 22%",
      sidebar: "150 30% 18%",
      sidebarFg: "50 25% 90%",
      sidebarAccent: "150 28% 24%",
    }),
    dark: buildDark({
      bg: "150 25% 8%",
      fg: "50 25% 90%",
      card: "150 25% 12%",
      border: "150 18% 22%",
      muted: "150 18% 16%",
      mutedFg: "50 15% 65%",
      primary: "45 65% 55%",
      primaryFg: "150 30% 10%",
      accent: "150 18% 20%",
      accentFg: "50 25% 88%",
      sidebar: "150 35% 6%",
      sidebarFg: "50 25% 90%",
      sidebarAccent: "150 25% 12%",
    }),
  },
  {
    id: "terracotta",
    label: "Terracotta",
    description: "Warm clay tones, cream cards and sage accents. Earthy and bright.",
    preview: { bg: "#f5ebde", sidebar: "#5a2e1f", accent: "#b8552c", text: "#3b1f15" },
    defaultAccent: "clay",
    light: buildLight({
      bg: "30 50% 94%",
      fg: "20 35% 18%",
      card: "30 55% 97%",
      border: "25 25% 84%",
      muted: "30 30% 91%",
      mutedFg: "20 15% 42%",
      primary: "18 60% 45%",
      primaryFg: "30 50% 97%",
      accent: "100 20% 86%",
      accentFg: "100 25% 28%",
      sidebar: "18 50% 22%",
      sidebarFg: "30 45% 92%",
      sidebarAccent: "18 40% 30%",
    }),
    dark: buildDark({
      bg: "20 25% 10%",
      fg: "30 30% 90%",
      card: "20 25% 14%",
      border: "20 18% 24%",
      muted: "20 18% 18%",
      mutedFg: "30 15% 65%",
      primary: "18 70% 60%",
      primaryFg: "20 30% 10%",
      accent: "20 18% 22%",
      accentFg: "30 30% 90%",
      sidebar: "18 30% 8%",
      sidebarFg: "30 30% 90%",
      sidebarAccent: "20 22% 14%",
    }),
  },
];

export const SKIN_BY_ID: Record<string, Skin> = Object.fromEntries(
  SKINS.map((s) => [s.id, s]),
);

// ─── Accents ────────────────────────────────────────────────────────────────
// When an accent is selected we override --primary, --ring, sidebar-ring,
// sidebar-primary, and the focus-visible ring. The skin's own accent
// surfaces (the soft tinted backgrounds) stay put so contrast holds.
export const ACCENTS: Accent[] = [
  { id: "teal",    label: "Teal",    hsl: "164 72% 40%", foregroundHsl: "0 0% 100%", borderHsl: "172 78% 27%" },
  { id: "navy",    label: "Navy",    hsl: "215 70% 32%", foregroundHsl: "0 0% 100%", borderHsl: "215 70% 26%" },
  { id: "sage",    label: "Sage",    hsl: "100 25% 38%", foregroundHsl: "0 0% 100%", borderHsl: "100 25% 30%" },
  { id: "rose",    label: "Rose",    hsl: "350 50% 55%", foregroundHsl: "0 0% 100%", borderHsl: "350 50% 45%" },
  { id: "taupe",   label: "Taupe",   hsl: "28 35% 40%",  foregroundHsl: "0 0% 100%", borderHsl: "28 35% 32%"  },
  { id: "camel",   label: "Camel",   hsl: "32 50% 45%",  foregroundHsl: "0 0% 100%", borderHsl: "32 50% 36%"  },
  { id: "brass",   label: "Brass",   hsl: "40 65% 48%",  foregroundHsl: "30 50% 12%", borderHsl: "40 65% 40%" },
  { id: "gold",    label: "Gold",    hsl: "45 70% 50%",  foregroundHsl: "30 50% 12%", borderHsl: "45 70% 42%" },
  { id: "clay",    label: "Clay",    hsl: "18 60% 48%",  foregroundHsl: "0 0% 100%", borderHsl: "18 60% 38%"  },
  { id: "plum",    label: "Plum",    hsl: "290 30% 42%", foregroundHsl: "0 0% 100%", borderHsl: "290 30% 34%" },
];

export const ACCENT_BY_ID: Record<string, Accent> = Object.fromEntries(
  ACCENTS.map((a) => [a.id, a]),
);

// ─── Font pairings ──────────────────────────────────────────────────────────
// Loaded on demand via `ensureFontPairing` (provider). The Inter pairing is
// already loaded by index.html so it costs nothing.
export const FONT_PAIRINGS: FontPairing[] = [
  {
    id: "inter",
    label: "Inter",
    sample: "Crisp, neutral, default ClassTrack feel.",
    body: "'Inter', system-ui, sans-serif",
    heading: "'Inter', system-ui, sans-serif",
  },
  {
    id: "lora-inter",
    label: "Lora + Inter",
    sample: "Editorial serif headings on a clean sans body.",
    body: "'Inter', system-ui, sans-serif",
    heading: "'Lora', Georgia, serif",
    googleFontsUrl: "https://fonts.googleapis.com/css2?family=Lora:wght@500;600;700&display=swap",
  },
  {
    id: "fraunces-inter",
    label: "Fraunces + Inter",
    sample: "Warm contemporary serif with a sturdy sans body.",
    body: "'Inter', system-ui, sans-serif",
    heading: "'Fraunces', Georgia, serif",
    googleFontsUrl: "https://fonts.googleapis.com/css2?family=Fraunces:wght@500;600;700&display=swap",
  },
  {
    id: "source",
    label: "Source Serif + Source Sans",
    sample: "Classic Adobe pairing — readable and academic.",
    body: "'Source Sans 3', system-ui, sans-serif",
    heading: "'Source Serif 4', Georgia, serif",
    googleFontsUrl:
      "https://fonts.googleapis.com/css2?family=Source+Sans+3:wght@400;500;600;700&family=Source+Serif+4:wght@500;600;700&display=swap",
  },
];

export const FONT_PAIRING_BY_ID: Record<string, FontPairing> = Object.fromEntries(
  FONT_PAIRINGS.map((f) => [f.id, f]),
);

// ─── Patterns ───────────────────────────────────────────────────────────────
// Inline SVG data URLs. Tile size is small to keep the data URL compact.
// All patterns use `currentColor` so we can control opacity + tint via CSS
// `color: var(--pattern-color)`. The pattern layer in AppLayout sets that.
function svgUrl(svg: string): string {
  return `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`;
}

const DOTTED_GRID = svgUrl(
  `<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24'><circle cx='2' cy='2' r='1' fill='currentColor'/></svg>`,
);

const DITSY_FLORAL = svgUrl(
  `<svg xmlns='http://www.w3.org/2000/svg' width='40' height='40'><g fill='currentColor'><circle cx='8' cy='8' r='1.4'/><circle cx='6' cy='10' r='1'/><circle cx='10' cy='10' r='1'/><circle cx='8' cy='12' r='1'/><circle cx='8' cy='6' r='1'/><circle cx='28' cy='28' r='1.4'/><circle cx='26' cy='30' r='1'/><circle cx='30' cy='30' r='1'/><circle cx='28' cy='32' r='1'/><circle cx='28' cy='26' r='1'/></g></svg>`,
);

const GINGHAM = svgUrl(
  `<svg xmlns='http://www.w3.org/2000/svg' width='20' height='20'><g fill='currentColor'><rect x='0' y='0' width='10' height='10' opacity='0.5'/><rect x='10' y='10' width='10' height='10' opacity='0.5'/></g></svg>`,
);

const TERRAZZO = svgUrl(
  `<svg xmlns='http://www.w3.org/2000/svg' width='60' height='60'><g fill='currentColor'><polygon points='5,2 9,4 7,9 3,7'/><polygon points='30,12 36,14 33,20 28,17'/><polygon points='50,30 55,34 52,40 47,36'/><polygon points='15,40 21,42 18,48 13,45'/><polygon points='42,50 47,52 45,58 40,55'/><circle cx='25' cy='35' r='2'/><circle cx='48' cy='10' r='1.5'/></g></svg>`,
);

const MARBLED = svgUrl(
  `<svg xmlns='http://www.w3.org/2000/svg' width='80' height='80'><g fill='none' stroke='currentColor' stroke-width='0.5'><path d='M0 30 Q 20 20 40 30 T 80 30'/><path d='M0 50 Q 20 40 40 50 T 80 50'/><path d='M0 70 Q 20 60 40 70 T 80 70'/><path d='M0 10 Q 20 0 40 10 T 80 10'/></g></svg>`,
);

export const PATTERNS: Pattern[] = [
  { id: "none",    label: "None",         url: "" },
  { id: "dotted",  label: "Dotted grid",  url: DOTTED_GRID },
  { id: "floral",  label: "Ditsy floral", url: DITSY_FLORAL },
  { id: "gingham", label: "Gingham",      url: GINGHAM },
  { id: "terrazzo",label: "Terrazzo",     url: TERRAZZO },
  { id: "marbled", label: "Marbled",      url: MARBLED },
];

export const PATTERN_BY_ID: Record<string, Pattern> = Object.fromEntries(
  PATTERNS.map((p) => [p.id, p]),
);
